// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    createHandlerChainRunner,
    Handler,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import translate from "@insite/client-framework/Translate";
import { setShippingInformation, SetShippingInformationApiParameter } from "@tps/App/Services/CartService";
import { ErrorApiDefaultMessage } from "@tps/XDL/constants";

type Parameter = {
    shippingInfo: SetShippingInformationApiParameter;
    onSuccess?: () => void;
    onError?: (error: string) => void;
};

type Props = {
    apiParameter: any;
    apiResult: any;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginUpdatingShippingInformation: HandlerType = props => {
    props.dispatch({
        type: "xShippingDetails/ResetIsUpdatingShippingDetails",
        isUpdating: true,
    } as any);
};

export const SendDataToApi: HandlerType = async props => {
    try {
        props.apiResult = await setShippingInformation(props.parameter.shippingInfo);
    } catch (error) {
        props.dispatch({
            type: "xShippingDetails/ResetIsUpdatingShippingDetails",
            isUpdating: false,
        } as any);
        const failedMessage = error.errorMessage || translate(ErrorApiDefaultMessage);
        props.parameter.onError?.(failedMessage);
        return false;
    }
};

export const DispatchCompleteUpdatingShippingInformation: HandlerType = props => {
    props.dispatch({
        type: "xShippingDetails/CompleteUpdatingWarehouseShippingDetails",
        shippingInfo: {
            warehouseId: props.parameter.shippingInfo.WarehouseId,
            shipViaId: props.parameter.shippingInfo.ShipViaId,
            shippingMethod: props.parameter.shippingInfo.ShippingMethod,
            termsType: props.parameter.shippingInfo.TermsType,
            billToName: props.parameter.shippingInfo.BillToName,
            address1: props.parameter.shippingInfo.Address1,
            address2: props.parameter.shippingInfo.Address2,
            city: props.parameter.shippingInfo.City,
            postalCode: props.parameter.shippingInfo.PostalCode,
            accountNumber: props.parameter.shippingInfo.AccountNumber,
            state: props.parameter.shippingInfo.State,
            notes: props.parameter.shippingInfo.Notes,
        },
        warehouseId: props.parameter.shippingInfo.WarehouseId,
        isUpdating: false,
    } as any);
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const chain = [
    DispatchBeginUpdatingShippingInformation,
    SendDataToApi,
    DispatchCompleteUpdatingShippingInformation,
    ExecuteOnSuccessCallback,
];

const setCartShippingInformation = createHandlerChainRunner(chain, "setCartShippingInformation");

export default setCartShippingInformation;
